import React, { useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { coy } from "react-syntax-highlighter/dist/esm/styles/prism";

const ApiDocsSection = () => {
  const [activeTab, setActiveTab] = useState("java");
  const pdfRef = useRef();

  const generatePDF = () => {
    const element = pdfRef.current;
    const opt = {
      margin: 0.5,
      filename: "API_Documentation.pdf",
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "portrait" }
    };
    html2pdf().from(element).set(opt).save();
  };

  // ------------------------
  // CODE SNIPPETS
  // ------------------------

  const javaExample = `import okhttp3.*;
import java.nio.file.*;
import com.fasterxml.jackson.databind.ObjectMapper;
import java.util.*;

public class ApiClientExample {
    private static final String API_URL = System.getenv("API_BASE_URL");
    private static final String API_KEY = System.getenv("API_KEY");

    public static void main(String[] args) throws Exception {

        // Load JSON template
        String templateJson = new String(Files.readAllBytes(Paths.get("template.json")));

        // Load DB values
        Map<String, Object> tableA = new HashMap<>();
        tableA.put("columnA", 744);
        tableA.put("columnB", "SSdefault");

        List<Map<String,Object>> tableBList = new ArrayList<>();
        tableBList.add(new HashMap<String, Object>() {{
            put("columnA", 56);
            put("columnB", "Multigrain");
            put("columnC", 300);
        }});

        // Replace placeholders
        String finalJson = templateJson
                .replace("{{tableA.columnA}}", tableA.get("columnA").toString())
                .replace("{{tableA.columnB}}", tableA.get("columnB").toString());

        // Prepare HTTP request
        OkHttpClient client = new OkHttpClient();
        MediaType JSON = MediaType.parse("application/json");

        RequestBody body = RequestBody.create(finalJson, JSON);

        Request request = new Request.Builder()
                .url(API_URL)
                .addHeader("x-api-key", API_KEY)
                .post(body)
                .build();

        Response response = client.newCall(request).execute();
        System.out.println("Response: " + response.body().string());
    }
}`;

  const phpExample = `<?php
$apiUrl = getenv("API_BASE_URL");
$apiKey = getenv("API_KEY");

// Load template
$template = file_get_contents("template.json");

// Replace values from DB
$template = str_replace("{{tableA.columnA}}", 744, $template);
$template = str_replace("{{tableA.columnB}}", "SSdefault", $template);

$ch = curl_init($apiUrl);
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => $template,
    CURLOPT_HTTPHEADER => [
        "Content-Type: application/json",
        "x-api-key: $apiKey"
    ],
    CURLOPT_RETURNTRANSFER => true
]);

$response = curl_exec($ch);
curl_close($ch);

echo $response;
?>`;

  const vfpExample = `* Visual FoxPro API POST Example

LOCAL lcJson, lcUrl, lcApiKey, loHttp, lcResponse

lcUrl = GETENV("API_BASE_URL")
lcApiKey = GETENV("API_KEY")

* Load JSON template
lcJson = FILETOSTR("template.json")

* Replace placeholders
lcJson = STRTRAN(lcJson, "{{tableA.columnA}}", "744")
lcJson = STRTRAN(lcJson, "{{tableA.columnB}}", "SSdefault")

* Create HTTP object
loHttp = CREATEOBJECT("MSXML2.ServerXMLHTTP")
loHttp.Open("POST", lcUrl, .F.)
loHttp.setRequestHeader("Content-Type", "application/json")
loHttp.setRequestHeader("x-api-key", lcApiKey)
loHttp.Send(lcJson)

lcResponse = loHttp.responseText
MESSAGEBOX(lcResponse)
`;

  const curlExample = `curl -X POST "$API_BASE_URL" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: $API_KEY" \\
  -d @template_resolved.json`;

  const snippets = {
    java: javaExample,
    php: phpExample,
    vfp: vfpExample,
    curl: curlExample
  };

  const jsonTemplate = `{
  "data": {
    "XID": "ABCD",
    "doubleWrapperAttribute1": "{{tableA.columnA}}",
    "strAttribute1": "{{tableA.columnB}}",
    "obList": [
      {
        "longWrapperAttribute1": "{{tableB.columnA}}",
        "strAttribute1": "{{tableB.columnB}}",
        "longWrapperAttribute2": "{{tableB.columnC}}"
      }
    ]
  }
}`;

  return (
    <div style={{ padding: 20, fontFamily: "Arial" }}>
      {/* PDF Button */}
      <button
        onClick={generatePDF}
        style={{
          background: "#007bff",
          padding: "10px 16px",
          color: "white",
          borderRadius: 5,
          border: "none",
          marginBottom: 20
        }}
      >
        Export as PDF
      </button>

      {/* EXPORTABLE CONTENT */}
      <div ref={pdfRef} id="pdf-content">
        <h1>Developer API Documentation</h1>

        <h2>1. Endpoint</h2>
        <SyntaxHighlighter language="bash" style={coy}>
{`POST https://api.handyreceipts.co.in/v3/genreceiptinfo`}
        </SyntaxHighlighter>

        <h2>2. Authentication</h2>
        <p>Send API key in header:</p>
        <SyntaxHighlighter language="bash" style={coy}>
{`x-api-key: YOUR_API_KEY`}
        </SyntaxHighlighter>

        <h2>3. JSON Template (Client Must Fill Dynamically)</h2>
        <div className="no-page-break">
        <SyntaxHighlighter language="json" style={coy}>
          {jsonTemplate}
        </SyntaxHighlighter>
</div>
        <h2>4. Code Examples</h2>

        {/* Tabs */}
        <div style={{ marginBottom: 10 }}>
          {["java", "php", "vfp", "curl"].map((lang) => (
            <button
              key={lang}
              onClick={() => setActiveTab(lang)}
              style={{
                marginRight: 7,
                padding: "6px 12px",
                background: activeTab === lang ? "#007bff" : "#eee",
                color: activeTab === lang ? "white" : "black",
                borderRadius: 4,
                border: "none"
              }}
            >
              {lang.toUpperCase()}
            </button>
          ))}
        </div>
<div className="no-page-break">
        <SyntaxHighlighter language={activeTab} style={coy}>
          {snippets[activeTab]}
        </SyntaxHighlighter>
        </div>
      </div>
    </div>
  );
};

export default ApiDocsSection;
